import { CheckCircle } from '@mui/icons-material';
import { alpha, Box, Chip, Typography, useTheme } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import CustomAutocomplete from '../../../../layout/styledcomponents/CustomAutoComplete';
import {
    Attendee,
    ReservationSpace,
    ReservationSpaceAttendee,
} from '../../../types/reservation';

type AttendeeOption = {
    key: string;
    label: string;
    email?: string;
    name?: string;
    isPlaceholder: boolean;
    isNoOptionsMarker?: boolean;
};

const NO_OPTIONS_ROW: AttendeeOption = {
    key: 'no_options',
    label: 'No options - type a placeholder',
    isPlaceholder: true,
    isNoOptionsMarker: true,
};

const defaultFilterOptions = createFilterOptions<AttendeeOption>();

export default function SpaceAttendees({
    space,
    handleAddSpaceForAttendees,
}: {
    space: any;
    handleAddSpaceForAttendees?: (space: any) => void;
}) {
    const theme = useTheme();
    const { control, setValue, getValues } = useFormContext();

    const spaceId = space.id;
    const sleepingCapacity = space.sleepingCapacity;

    const spaces: ReservationSpace[] =
        useWatch({
            control,
            name: 'spaces',
        }) ?? [];

    const staffAttending: Attendee[] =
        useWatch({
            control,
            name: 'staffAttending',
        }) ?? [];
    const externalAttendees: Attendee[] =
        useWatch({
            control,
            name: 'externalAttendees',
        }) ?? [];

    const attendeeOptions: AttendeeOption[] = [
        ...staffAttending,
        ...externalAttendees,
    ].map((attendee) => ({
        key: attendee.email,
        label: `${attendee.firstName} ${attendee.lastName} (${attendee.email})`,
        email: attendee.email,
        isPlaceholder: false,
    }));

    const attendeeKeysAssignedToOtherSpaces: string[] = spaces
        .filter((space) => space.id !== spaceId)
        .flatMap((space) =>
            (space.attendees ?? []).map((attendee) =>
                attendee.email
                    ? attendee.email
                    : (attendee.name ?? '').trim().toLowerCase(),
            ),
        );

    const selectedOptions: AttendeeOption[] = (
        spaces.find((space) => space.id === spaceId)?.attendees ?? []
    ).map((attendee) => {
        return (
            attendeeOptions.find(
                (option) => option.email === attendee.email,
            ) ?? {
                key: (attendee.name?.trim() ?? '').toLowerCase(),
                label: (attendee.name ?? '').trim(),
                name: attendee.name,
                isPlaceholder: true,
            }
        );
    });

    const handleChange = (attendeeOptions: AttendeeOption[]) => {
        const assignable = attendeeOptions.filter(
            (option) => !option.isNoOptionsMarker,
        );
        const deduped: AttendeeOption[] = [];
        const existingKeys: string[] = [];
        for (const option of assignable) {
            if (existingKeys.includes(option.key)) continue;
            existingKeys.push(option.key);
            deduped.push(option);
        }

        const capped =
            sleepingCapacity > 0 ? deduped.slice(0, sleepingCapacity) : deduped;

        const attendees: ReservationSpaceAttendee[] = capped.map((option) =>
            option.isPlaceholder
                ? { name: option.name }
                : { email: option.email },
        );

        handleAddSpaceForAttendees?.(space);
        const currentSpaces: ReservationSpace[] =
            (getValues('spaces') as ReservationSpace[]) ?? spaces;

        const updated =
            attendees.length === 0 && space.cleaningFee === 0
                ? currentSpaces.filter(
                      (existingSpace) => existingSpace.id !== spaceId,
                  )
                : currentSpaces.map((existingSpace) =>
                      existingSpace.id === spaceId
                          ? { ...existingSpace, attendees }
                          : existingSpace,
                  );
        setValue('spaces', updated, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
        });
    };

    const getPlaceHolderOption = (name: string): AttendeeOption | null => {
        const trimmed = name.trim();
        if (!trimmed) return null;
        return {
            key: trimmed.toLowerCase(),
            label: trimmed,
            name: trimmed,
            isPlaceholder: true,
        };
    };

    const capacityReached =
        sleepingCapacity > 0 && selectedOptions.length >= sleepingCapacity;

    const disableCloseOnSelect =
        sleepingCapacity === 0 || selectedOptions.length < sleepingCapacity - 1;

    const selectableOptions = attendeeOptions.filter(
        (option) => !attendeeKeysAssignedToOtherSpaces.includes(option.key),
    );

    const autocompleteOptions =
        selectableOptions.length === 0 ? [NO_OPTIONS_ROW] : selectableOptions;

    const capacityText =
        sleepingCapacity > 0
            ? `${selectedOptions.length} / ${sleepingCapacity} guest${
                  sleepingCapacity === 1 ? '' : 's'
              }`
            : undefined;

    return (
        <Box sx={{ width: '100%' }}>
            <CustomAutocomplete<AttendeeOption, true, false, true>
                multiple
                freeSolo
                disableCloseOnSelect={disableCloseOnSelect}
                filterSelectedOptions
                getOptionDisabled={(option) =>
                    capacityReached || Boolean(option.isNoOptionsMarker)
                }
                filterOptions={(_, state) => {
                    const input = state.inputValue?.trim() ?? '';

                    const filteredFromList =
                        selectableOptions.length === 0
                            ? []
                            : defaultFilterOptions(selectableOptions, state);

                    const selectedKeys = new Set(
                        selectedOptions.map((s) => s.key),
                    );
                    const notYetSelected = filteredFromList.filter(
                        (option) => !selectedKeys.has(option.key),
                    );

                    const creatable =
                        input.length > 0 ? getPlaceHolderOption(input) : null;

                    const isDuplicateCreatable =
                        creatable != null &&
                        (notYetSelected.some(
                            (option) =>
                                !option.isNoOptionsMarker &&
                                (option.key === creatable.key ||
                                    option.label.toLowerCase() ===
                                        creatable.label.toLowerCase()),
                        ) ||
                            selectedOptions.some(
                                (selected) => selected.key === creatable.key,
                            ));

                    const result: AttendeeOption[] = [...notYetSelected];

                    if (
                        creatable &&
                        !isDuplicateCreatable &&
                        !capacityReached
                    ) {
                        result.push(creatable);
                    }

                    if (result.length === 0) {
                        return [NO_OPTIONS_ROW];
                    }

                    return result;
                }}
                options={autocompleteOptions}
                value={selectedOptions}
                onKeyDown={(e) => {
                    if (capacityReached && e.key === 'Enter') {
                        e.preventDefault();
                    }
                }}
                onChange={(_, entries) => {
                    const normalised: AttendeeOption[] = entries
                        .map((entry) => {
                            return typeof entry === 'string'
                                ? getPlaceHolderOption(entry)
                                : entry;
                        })
                        // Using the type guard because makePlaceholderOption can return null and
                        // a plain .filter(Boolean) does not narrow AttendeeOption | null union for TypeScript.
                        .filter((entry): entry is AttendeeOption =>
                            Boolean(entry),
                        )
                        .filter((entry) => !entry.isNoOptionsMarker)
                        .filter((entry) => {
                            return !attendeeKeysAssignedToOtherSpaces.includes(
                                entry.key,
                            );
                        });
                    handleChange(normalised);
                }}
                getOptionLabel={(option) =>
                    typeof option === 'string' ? option : (option?.label ?? '')
                }
                isOptionEqualToValue={(option, v) => option?.key === v?.key}
                renderTags={(tagValue, getTagProps) =>
                    tagValue.map((opt, i) => (
                        <Chip
                            {...getTagProps({ index: i })}
                            key={opt.key}
                            label={opt.label}
                            size='small'
                            variant={opt.isPlaceholder ? 'outlined' : 'filled'}
                            color={opt.isPlaceholder ? 'default' : 'primary'}
                        />
                    ))
                }
                label=''
                helperText={undefined}
                placeholder={
                    selectedOptions.length === 0
                        ? 'Select attendees or type a placeholder'
                        : ''
                }
                fontSize='12px'
                optionFontSize='12px'
                helperTextFontSize='12px'
                fontWeight={500}
            />

            {(capacityText || capacityReached) && (
                <Box
                    sx={{
                        mt: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        minHeight: 24,
                    }}
                >
                    <Typography
                        variant='caption'
                        sx={{
                            fontSize: 12,
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                        }}
                    >
                        {capacityText ?? ''}
                    </Typography>

                    {capacityReached ? (
                        <Chip
                            icon={
                                <CheckCircle
                                    sx={{
                                        mr: 1,
                                        fontSize: { xs: 10, sm: 12 },
                                    }}
                                />
                            }
                            label='Full'
                            size='small'
                            sx={{
                                borderRadius: 1.5,
                                fontSize: 12,
                                color: theme.palette.custom.sapphire,
                                backgroundColor: alpha(
                                    theme.palette.custom.sapphire,
                                    0.18,
                                ),
                                '& .MuiChip-icon': {
                                    color: theme.palette.custom.sapphire,
                                    fontSize: 16,
                                },
                            }}
                        />
                    ) : (
                        <Box />
                    )}
                </Box>
            )}
        </Box>
    );
}
